import * as Sentry from '@sentry/react';
import type { EditApplierRegistry, PendingEdit } from './applier-registry';
import type { EditDeltaData, EditEntityKind, EditRequest, EditTarget, ReviewMode } from './types';

interface ToolInputAvailableChunk {
  type: 'tool-input-available';
  toolCallId: string;
  toolName: string;
  input: Record<string, unknown>;
}

function isToolInputAvailableChunk(v: unknown): v is ToolInputAvailableChunk {
  if (typeof v !== 'object' || v === null) return false;
  const u = v as Record<string, unknown>;
  return (
    u['type'] === 'tool-input-available' &&
    typeof u['toolName'] === 'string' &&
    typeof u['input'] === 'object' &&
    u['input'] !== null
  );
}

function validateToolInput(toolName: string, input: Record<string, unknown>): void {
  if (toolName === 'editor.replaceText') {
    if (typeof input['entityId'] !== 'string' || typeof input['text'] !== 'string' || typeof input['target'] !== 'object' || input['target'] === null) {
      const err = new Error(`Invalid replaceText input: missing entityId, target, or text`);
      Sentry.captureException(err);
      throw err;
    }
    return;
  }
  if (toolName === 'editor.embedCanvas') {
    if (typeof input['entityId'] !== 'string' || typeof input['target'] !== 'object' || input['target'] === null) {
      const err = new Error(`Invalid embedCanvas input: missing entityId or target`);
      Sentry.captureException(err);
      throw err;
    }
  }
}

function toolToEditData(
  chunk: ToolInputAvailableChunk,
  entityKind: EditEntityKind,
): EditDeltaData | null {
  const input = chunk.input;
  validateToolInput(chunk.toolName, input);
  const entityId = input['entityId'] as string;
  const target = input['target'] as EditTarget;
  if (chunk.toolName === 'editor.replaceText') {
    const text = input['text'] as string;
    return {
      op: 'replace-text',
      entityKind,
      entityId,
      target,
      payload: { op: 'replace-text', text },
    };
  }
  if (chunk.toolName === 'editor.embedCanvas') {
    return {
      op: 'embed-canvas',
      entityKind,
      entityId,
      target,
      payload: {
        op: 'embed-canvas',
        canvasId: input['entityId'] as string,
      },
    };
  }
  return null;
}

interface ToolInputDeltaChunk {
  type: 'tool-input-delta';
  toolCallId: string;
  inputTextDelta: string;
}

function isToolInputDeltaChunk(v: unknown): v is ToolInputDeltaChunk {
  if (typeof v !== 'object' || v === null) return false;
  const u = v as Record<string, unknown>;
  return u['type'] === 'tool-input-delta' && typeof u['inputTextDelta'] === 'string';
}

function isErrorChunk(v: unknown): v is { type: 'error'; errorText?: string } {
  return typeof v === 'object' && v !== null && (v as Record<string, unknown>)['type'] === 'error';
}

export async function consumeEditStream(
  stream: ReadableStream<Uint8Array>,
  req: EditRequest,
  corrId: string,
  registry: EditApplierRegistry,
  // Optional pending created upfront (e.g. chart's loading widget, shown before
  // the stream produces anything). When provided, begin() is NOT called again.
  initialPending?: PendingEdit,
  mode?: ReviewMode,
  // Per-delta pacing: delay (ms) applied after each text delta to slow the reveal to a
  // calmer rate. Defaults to 0 (no pacing) so tests run instantly; production passes a value.
  revealDelayMs = 0,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  // Cancellation: when the caller aborts (e.g. the editor unmounts on a tab switch),
  // stop consuming. The caller owns restoring the original via the pending it received
  // through onPending — so we do NOT reject here on abort.
  signal?: AbortSignal,
  // Invoked once with the real pending the instant begin() produces it, so the caller can
  // reject it synchronously on abort (the store only holds the requesting stub mid-stream).
  onPending?: (pending: PendingEdit) => void,
): Promise<PendingEdit | undefined> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const applier = registry.forKind(req.entityKind);
  let pending: PendingEdit | undefined = initialPending;
  let failed = false;
  let accumulated = '';
  const ensurePending = (): PendingEdit | undefined => {
    if (pending || !applier) return pending;
    pending = applier.begin(req, mode);
    onPending?.(pending);
    return pending;
  };

  try {
    for (;;) {
      if (signal?.aborted) break;
      const { value, done } = await reader.read();
      if (done) break;
      const text = decoder.decode(value);
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        continue;
      }
      // A server-side error chunk (e.g. "Could not generate a valid chart") ends
      // the run — throw so the orchestrator surfaces it and we clean up below.
      if (isErrorChunk(parsed)) {
        failed = true;
        throw new Error(parsed.errorText ?? 'Edit action failed');
      }
      // Text deltas (editor.replaceText) stream per-token. Accumulate and apply the FULL
      // accumulated text each time so the reveal is incremental, not all-at-once.
      if (isToolInputDeltaChunk(parsed) && parsed.toolCallId === `tc-${corrId}`) {
        accumulated += parsed.inputTextDelta;
        if (!applier) continue;
        if (signal?.aborted) break;
        pending = ensurePending();
        if (!pending) continue;
        await applier.applyChunk(pending, {
          op: 'replace-text',
          entityKind: req.entityKind,
          entityId: req.entityId,
          target: req.target,
          payload: { op: 'replace-text', text: accumulated },
        });
        if (revealDelayMs > 0) await sleep(revealDelayMs);
        continue;
      }
      if (!isToolInputAvailableChunk(parsed) || parsed.toolCallId !== `tc-${corrId}`) continue;
      if (!applier) continue;
      const knownTool = parsed.toolName === 'editor.replaceText' || parsed.toolName === 'editor.embedCanvas';
      if (!knownTool) continue;
      pending = ensurePending();
      if (!pending) continue;
      const data = toolToEditData(parsed, req.entityKind);
      if (!data) continue;
      await applier.applyChunk(pending, data);
    }
  } catch (e) {
    failed = true;
    throw e;
  } finally {
    reader.releaseLock();
    // If we opened a pending preview (e.g. the chart loading widget) but the run
    // errored, reject it so no decoration is left stranded on the editor.
    if (failed && pending && applier) applier.reject(pending);
  }
  return pending;
}
